import React from 'react';
import DocsLayout from '../../layout/DocsLayout';
import SEO from '../../components/SEO';
import CodeBlock from '../../components/CodeBlock';
import MultiLanguageCodeBlock from '../../components/MultiLanguageCodeBlock';
import './docs.css';

const GiftCards = () => {


  const onThisPageItems = [
    { href: '#overview', label: 'Overview' },
    { href: '#merchant-types', label: 'Merchant Types & Gift Card Types' },
    { href: '#templates', label: 'Gift Card Templates' },
    { href: '#instances', label: 'Gift Card Instances' },
    { href: '#redemption', label: 'Redemption Flow' },
    { href: '#error-handling', label: 'Error Handling' },
    { href: '#best-practices', label: 'Best Practices' },
  ];

  return (
    <>
      <SEO
        title="Gift Cards API - Otto Africa API Documentation"
        description="Learn how to create, manage, and redeem digital gift cards using Otto Africa API. Complete guide with code examples."
        keywords="gift card API, digital gift cards, gift card management, gift card redemption, Otto gift cards"
        url="https://ottoafrica.com/docs/gift-cards"
      />
      <DocsLayout
        currentPage="/docs/gift-cards"
      
      onThisPageItems={onThisPageItems}
      nutshell="Otto supports two gift card types: templates (reusable designs) and instances (individual purchased cards). Use the API to create templates, manage instances, verify balances, and process redemptions."
    >
      <div className="docs-content">
        <h1 id="overview">Gift Cards API</h1>

        <p>
          The Gift Cards API allows you to programmatically create, manage, and redeem gift cards
          through Otto's platform. This is perfect for e-commerce integrations, loyalty programs,
          and custom gift card solutions.
        </p>

        <h2>Overview</h2>

        <p>
          Otto supports two types of gift cards:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6">
          <li><strong>Gift Card Templates:</strong> Reusable designs that define denominations, styling, and redemption rules</li>
          <li><strong>Gift Card Instances:</strong> Individual gift cards purchased by customers with unique codes</li>
        </ul>

        <h2 id="merchant-types">Merchant Types & Gift Card Types</h2>

        <p>
          Otto distinguishes two <strong>merchant categories</strong> (set at signup): <strong>Merchant</strong> and <strong>Investment provider</strong>.
        </p>
        <ul className="list-disc list-inside space-y-2 mb-4">
          <li><strong>Merchant:</strong> Can create and manage <strong>regular gift cards</strong> only (templates and instances).</li>
          <li><strong>Investment provider:</strong> Can create both <strong>regular gift cards</strong> and <strong>investment certificates</strong> (investment gift products, e.g. held in trust, NAV-based).</li>
        </ul>
        <p>
          Gift card templates have a <code>certificate_type</code>: <code>STANDARD</code> (regular gift card) or <code>INVESTMENT</code> (investment certificate). When creating a template via the API, you may send <code>certificate_type</code>; default is <code>STANDARD</code>. Only businesses registered as <strong>Investment providers</strong> may create templates with <code>certificate_type: "INVESTMENT"</code>; otherwise the API returns <strong>403</strong>.
        </p>
        <p>
          Listing templates (e.g. <code>GET /merchant/giftcards/templates</code>) returns all templates for your business; each includes <code>certificate_type</code>. For the Investment Certificates API (list certificates, reconciliation, sync value), see <a href="/docs/investment-certificates" className="text-otto-blue hover:underline">Investment Certificates</a> — those endpoints are only available to businesses registered as Investment providers.
        </p>
        <p>
          <strong>API keys:</strong> Keys are scoped by your business type. Keys created for a <strong>Merchant</strong> business can call gift card, transactions, and related endpoints but <em>cannot</em> call investment certificate endpoints. Keys created for an <strong>Investment provider</strong> business have full access, including investment certificate endpoints. In the merchant portal (Settings → API Keys), each key shows its type (Merchant or Investment provider).
        </p>

        <h2 id="templates">Gift Card Templates</h2>

        <p>
          Templates define the structure and appearance of gift cards. Create templates once,
          then use them to generate individual gift card instances.
        </p>

        <h3>Create a Gift Card Template</h3>

        <div className="api-endpoint">
          <span className="method post">POST</span>
          <strong>/merchant/giftcard-templates</strong><br />
          <span className="description">Create a new gift card template</span>
        </div>

        <MultiLanguageCodeBlock
          requestMethod="POST"
          requestUrl="/merchant/giftcard-templates"
          examples={{
            curl: `curl -X POST "https://api.ottoafrica.com/merchant/giftcard-templates" \\
  -H "Authorization: Bearer your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Birthday Special",
    "description": "Perfect for birthday celebrations",
    "denominations": [25, 50, 100, 200],
    "currency": "GHS",
    "background_color": "#FF6B6B",
    "text_color": "#FFFFFF",
    "expires_in_days": 365
  }'`,
            javascript: `const response = await fetch('https://api.ottoafrica.com/merchant/giftcard-templates', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your_api_key',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Birthday Special',
    description: 'Perfect for birthday celebrations',
    denominations: [25, 50, 100, 200],
    currency: 'GHS',
    background_color: '#FF6B6B',
    text_color: '#FFFFFF',
    expires_in_days: 365
  })
});

const data = await response.json();`,
            python: `import requests

response = requests.post(
    'https://api.ottoafrica.com/merchant/giftcard-templates',
    headers={
        'Authorization': 'Bearer your_api_key',
        'Content-Type': 'application/json'
    },
    json={
        'name': 'Birthday Special',
        'description': 'Perfect for birthday celebrations',
        'denominations': [25, 50, 100, 200],
        'currency': 'GHS',
        'background_color': '#FF6B6B',
        'text_color': '#FFFFFF',
        'expires_in_days': 365
    }
)

data = response.json()`
          }}
                />

        <h3>Template Parameters</h3>

        <table className="docs-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Required</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>name</code></td>
              <td>string</td>
              <td>Yes</td>
              <td>Template name (max 100 characters)</td>
            </tr>
            <tr>
              <td><code>denominations</code></td>
              <td>array</td>
              <td>Yes</td>
              <td>Available values in the specified currency</td>
            </tr>
            <tr>
              <td><code>currency</code></td>
              <td>string</td>
              <td>Yes</td>
              <td>Currency code (GHS, USD, etc.)</td>
            </tr>
            <tr>
              <td><code>background_color</code></td>
              <td>string</td>
              <td>No</td>
              <td>Hex color code for card background</td>
            </tr>
            <tr>
              <td><code>text_color</code></td>
              <td>string</td>
              <td>No</td>
              <td>Hex color code for text</td>
            </tr>
            <tr>
              <td><code>expires_in_days</code></td>
              <td>integer</td>
              <td>No</td>
              <td>Days until gift card expires (default: 365)</td>
            </tr>
          </tbody>
        </table>

        <h3>List Gift Card Templates</h3>

        <div className="api-endpoint">
          <span className="method get">GET</span>
          <strong>/merchant/giftcard-templates</strong><br />
          <span className="description">List all gift card templates</span>
        </div>

        <CodeBlock
          language="bash"
          code={`curl -X GET "https://api.ottoafrica.com/merchant/giftcard-templates?page=1&per_page=15" \\
  -H "Authorization: Bearer your_api_key"`}
        />

        <h3>Update Gift Card Template</h3>

        <div className="api-endpoint">
          <span className="method put">PUT</span>
          <strong>/merchant/giftcard-templates/{`{id}`}</strong><br />
          <span className="description">Update an existing gift card template</span>
        </div>

        <CodeBlock
          language="bash"
          code={`curl -X PUT "https://api.ottoafrica.com/merchant/giftcard-templates/123" \\
  -H "Authorization: Bearer your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Updated Birthday Special",
    "is_active": true
  }'`}
        />

        <h2 id="instances">Gift Card Instances</h2>

        <p>
          Once you have templates, you can create individual gift card instances for customers.
          Each instance has a unique code and can be redeemed through Otto's mobile apps.
        </p>

        <h3>List Gift Card Instances</h3>

        <div className="api-endpoint">
          <span className="method get">GET</span>
          <strong>/merchant/giftcards</strong><br />
          <span className="description">List all purchased gift card instances</span>
        </div>

        <CodeBlock
          language="bash"
          code={`curl -X GET "https://api.ottoafrica.com/merchant/giftcards?page=1&per_page=20&status=active" \\
  -H "Authorization: Bearer your_api_key"`}
        />

        <h3>Verify Gift Card Balance</h3>

        <div className="api-endpoint">
          <span className="method get">GET</span>
          <strong>/merchant/giftcards/{`{code}`}/verify</strong><br />
          <span className="description">Check gift card balance and status</span>
        </div>

        <CodeBlock
          language="bash"
          code={`curl -X GET "https://api.ottoafrica.com/merchant/giftcards/ABC123456/verify" \\
  -H "Authorization: Bearer your_api_key"`}
        />

        <div className="docs-alert info">
          <strong>Response:</strong> Returns current balance, status, and expiration date.
        </div>

        <h2 id="redemption">Redeem a Gift Card</h2>

        <div className="docs-alert warning">
          <strong>Currency Note:</strong> All currency values are stored in Pesewas (1 GHS = 100 Pesewas).
        </div>

        <div className="api-endpoint">
          <span className="method post">POST</span>
          <strong>/merchant/giftcards/{`{code}`}/redeem</strong><br />
          <span className="description">Redeem gift card for specified amount</span>
        </div>

        <MultiLanguageCodeBlock
          requestMethod="POST"
          requestUrl="/merchant/giftcards/{code}/redeem"
          examples={{
            curl: `curl -X POST "https://api.ottoafrica.com/merchant/giftcards/ABC123456/redeem" \\
  -H "Authorization: Bearer your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 25.00,
    "transaction_reference": "TXN-12345"
  }'`,
            javascript: `const response = await fetch('https://api.ottoafrica.com/merchant/giftcards/ABC123456/redeem', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your_api_key',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    amount: 25.00,
    transaction_reference: 'TXN-12345'
  })
});

const data = await response.json();`,
            python: `import requests

response = requests.post(
    'https://api.ottoafrica.com/merchant/giftcards/ABC123456/redeem',
    headers={
        'Authorization': 'Bearer your_api_key',
        'Content-Type': 'application/json'
    },
    json={
        'amount': 25.00,
        'transaction_reference': 'TXN-12345'
    }
)

data = response.json()`
          }}
                />

        <h3>Redemption Flow</h3>

        <p>
          The typical gift card redemption flow involves:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6">
          <li><strong>Verify:</strong> Check if the gift card exists and has sufficient balance</li>
          <li><strong>Redeem:</strong> Process the redemption and update balance</li>
          <li><strong>Confirm:</strong> Receive webhook notification of successful redemption</li>
        </ol>

        <h2 id="error-handling">Error Handling</h2>

        <p>
          Common gift card errors and how to handle them:
        </p>

        <div className="docs-alert error">
          <strong>INVALID_CODE:</strong> The gift card code doesn't exist. Ask the customer to check the code.
        </div>

        <div className="docs-alert error">
          <strong>INSUFFICIENT_BALANCE:</strong> The gift card doesn't have enough balance for the transaction.
        </div>

        <div className="docs-alert error">
          <strong>EXPIRED:</strong> The gift card has expired and can no longer be used.
        </div>

        <div className="docs-alert warning">
          <strong>ALREADY_REDEEMED:</strong> The gift card has already been fully redeemed.
        </div>

        <h2 id="best-practices">Best Practices</h2>

        <h3>Security</h3>

        <ul className="list-disc list-inside space-y-2 mb-6">
          <li>Always verify gift card balance before redemption</li>
          <li>Store gift card codes securely (never in logs or client-side storage)</li>
          <li>Use idempotent operations for redemption to prevent double-charging</li>
          <li>Implement proper error handling for all API responses</li>
        </ul>

        <h3>Performance</h3>

        <ul className="list-disc list-inside space-y-2 mb-6">
          <li>Cache gift card template information to reduce API calls</li>
          <li>Use pagination for large lists of gift card instances</li>
          <li>Implement retry logic with exponential backoff</li>
          <li>Monitor your API usage in the Merchant Portal</li>
        </ul>

        <div className="docs-alert success">
          <strong>Need Help?</strong> Check the <a href="https://api.ottoafrica.com/api/docs" className="underline" target="_blank" rel="noopener noreferrer">API Reference</a> for complete endpoint documentation,
          or visit our <a href="/docs/support" className="underline">Support page</a>.
        </div>
      </div>
    </DocsLayout>
    </>
  );
};

export default GiftCards;
